import { FormattedMessage } from 'react-intl';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import Divider from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconName } from 'src/components/Icon/Icon.types';
import { Text } from 'src/components/Text';
import { GithubLanguage, GithubRepositoryInfo, GithubRepositoryVisibility } from 'src/orchd-client';
import { DropdownMenu } from 'src/components/DropdownMenu';

interface Props {
  repos: GithubRepositoryInfo[];
  onDeployToNewWebsite: (repo: GithubRepositoryInfo) => void;
  onDeployToExistingWebsite: (repo: GithubRepositoryInfo) => void;
}

const languageToIcon: Partial<Record<GithubLanguage, IconName>> = {
  [GithubLanguage.GO]: 'go',
  [GithubLanguage.JAVA]: 'java',
  [GithubLanguage.JAVASCRIPT]: 'javascript',
  [GithubLanguage.PYTHON]: 'python',
  [GithubLanguage.RUBY]: 'ruby',
  [GithubLanguage.RUST]: 'rust',
  [GithubLanguage.TYPESCRIPT]: 'typescript',
};

const visibilityToIcon: Record<GithubRepositoryVisibility, IconName> = {
  [GithubRepositoryVisibility.private]: 'lock',
  [GithubRepositoryVisibility.internal]: 'security',
  [GithubRepositoryVisibility.public]: 'globe',
};

export const AppRepos = ({ repos, onDeployToNewWebsite, onDeployToExistingWebsite }: Props) => {
  return (
    <>
      <Text transform="uppercase" size="xs" marginBottom="lg" color="grey">
        <FormattedMessage
          id="git.projects.your_repositories_heading"
          defaultMessage="{count, plural, one {<b>#</b> repository} other {<b>#</b> repositories}}"
          values={{ count: repos.length, b: (bolded) => <b>{bolded}</b> }}
        />
      </Text>
      <Card padding={{ xs: 'lg', md: '2xl' }}>
        {repos.map((repo, index) => (
          <>
            <Box>
              <Box d="row" j="space-between" a="center" px={{ xs: 'sm', md: 'xl' }} rowGap="lg">
                <Box w={{ xs: 12, md: 3 }} d="row" wrap="nowrap" a="center">
                  <Box w="48px" d="column" j="center">
                    {repo?.owner?.avatar_url && (
                      <img
                        src={repo.owner?.avatar_url}
                        alt=""
                        width={16}
                        height={24}
                        loading="lazy"
                        style={{
                          marginRight: '8px',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Box>
                  <Box w="auto" d="column">
                    <Text size="lg" marginBottom="xs">
                      {repo.name}
                    </Text>
                    <Box d="row" a="center">
                      <Icon name={languageToIcon[repo.language] ?? 'code_24'} size="md" marginRight="xs" />
                      <Text size="xs" color="grey">
                        {repo.language}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                <Box
                  w={{ xs: 12, md: 9 }}
                  d="row"
                  j={{ xs: 'flex-start', md: 'space-between' }}
                  a="center"
                  gap={{ xs: 'lg', md: '0' }}
                >
                  <Text size="sm">{repo.owner.login}</Text>
                  <Box d="row" a="center" w="auto">
                    <Icon name="github_branch" marginRight="xs" size="md" />
                    <Text size="sm">{repo.default_branch}</Text>
                  </Box>
                  <Box d="row" a="center" w="auto">
                    <Icon name={visibilityToIcon[repo.visibility]} marginRight="xs" size="md" color="grey" />
                    <Text size="sm" color="grey">
                      {repo.visibility.charAt(0).toUpperCase() + String(repo.visibility).slice(1)}
                    </Text>
                  </Box>
                  <DropdownMenu
                    minWidth={200}
                    options={[
                      {
                        label: (
                          <FormattedMessage
                            id="git.deploy_to_an_existing_website"
                            defaultMessage="To an existing website"
                          />
                        ),
                        onClick: () => onDeployToExistingWebsite(repo),
                      },
                      {
                        label: <FormattedMessage id="git.deploy_to_a_new_website" defaultMessage="To a new website" />,
                        onClick: () => onDeployToNewWebsite(repo),
                      },
                    ]}
                    button={
                      <Button variant="secondary" size="small" icon="chevron down" iconPosition="right">
                        <FormattedMessage id="git.deploy" defaultMessage="Deploy" />
                      </Button>
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Divider marginBottom={index === repos.length - 1 ? undefined : 'xl'} marginTop="xl" />
          </>
        ))}
      </Card>
    </>
  );
};
